
import React, { useState, useEffect, Suspense } from 'react';
import Sidebar from './components/Sidebar';
import ChatView from './components/ChatView';
import ImageView from './components/ImageView';
import VideoView from './components/VideoView';
import SearchView from './components/SearchView';
import MapsView from './components/MapsView';
import AudioView from './components/AudioView';
import DictionaryView from './components/DictionaryView';
import TranslateView from './components/TranslateView';
import UpgradeView from './components/UpgradeView';
import AuthView from './components/AuthView';
import ProfileView from './components/ProfileView';
import ErrorBoundary from './components/ErrorBoundary';
import { ViewType, FREE_DAILY_LIMIT, UserProfile, AppLanguage, ChatbotPersonality, AppTheme } from './types';
import { Loader2, Menu, Sparkles } from 'lucide-react';

const App: React.FC = () => {
  const [currentView, setCurrentView] = useState<ViewType>(ViewType.CHAT);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>(() => {
    const saved = localStorage.getItem('zomigenai_search_history');
    return saved ? JSON.parse(saved) : [];
  });

  const [user, setUser] = useState<UserProfile>(() => {
    const saved = localStorage.getItem('zomigenai_user');
    const today = new Date().toDateString();
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.lastResetDate !== today) {
        return { ...parsed, dailyCreditsUsed: 0, lastResetDate: today };
      }
      return parsed;
    }
    return { 
      isAuthenticated: false, 
      isPro: false, 
      dailyCreditsUsed: 0, 
      lastResetDate: today, 
      language: 'en', 
      theme: 'dark', 
      personality: 'professional'
    };
  });

  // theme management
  useEffect(() => {
    const root = window.document.documentElement;
    const applyTheme = (theme: AppTheme) => {
      root.classList.remove('light', 'dark');
      if (theme === 'system') {
        const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        root.classList.add(systemDark ? 'dark' : 'light');
      } else {
        root.classList.add(theme);
      }
    };
    applyTheme(user.theme);
    if (user.theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => applyTheme('system');
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [user.theme]);

  useEffect(() => {
    localStorage.setItem('zomigenai_user', JSON.stringify(user));
  }, [user]);

  const addToHistory = (query: string) => {
    setSearchHistory(prev => {
      const filtered = prev.filter(h => h.toLowerCase() !== query.toLowerCase());
      const newHistory = [query, ...filtered].slice(0, 10);
      localStorage.setItem('zomigenai_search_history', JSON.stringify(newHistory));
      return newHistory;
    });
  };

  const useCredit = () => {
    if (user.isPro) return true;
    if (user.dailyCreditsUsed < FREE_DAILY_LIMIT) {
      setUser(prev => ({ ...prev, dailyCreditsUsed: prev.dailyCreditsUsed + 1 }));
      return true;
    } else {
      setCurrentView(ViewType.SUBSCRIPTION);
      return false;
    }
  };

  const renderView = () => {
    switch (currentView) {
      case ViewType.CHAT: return <ChatView onUseCredit={useCredit} isPro={user.isPro} language={user.language} personality={user.personality} user={user} onNavigate={setCurrentView} />;
      case ViewType.IMAGE: return <ImageView onUseCredit={useCredit} isPro={user.isPro} language={user.language} />;
      case ViewType.VIDEO: 
        if (!user.isPro) return <UpgradeView isPro={false} onUpgrade={() => setUser(p => ({...p, isPro: true}))} language={user.language} />;
        return <VideoView onUseCredit={useCredit} language={user.language} />;
      case ViewType.SEARCH: return <SearchView onUseCredit={useCredit} isPro={user.isPro} language={user.language} onSearchPerformed={addToHistory} />;
      case ViewType.MAPS: return <MapsView onUseCredit={useCredit} language={user.language} />;
      case ViewType.AUDIO: return <AudioView onUseCredit={useCredit} language={user.language} />;
      case ViewType.DICTIONARY: return <DictionaryView onUseCredit={useCredit} language={user.language} onSearchPerformed={addToHistory} />;
      case ViewType.TRANSLATE: return <TranslateView onUseCredit={useCredit} isPro={user.isPro} language={user.language} />;
      case ViewType.SUBSCRIPTION: return <UpgradeView isPro={user.isPro} onUpgrade={() => setUser(p => ({...p, isPro: true}))} language={user.language} />;
      case ViewType.PROFILE: return <ProfileView user={user} language={user.language} onUpdate={(u) => setUser(p => ({...p, ...u}))} onBack={() => setCurrentView(ViewType.CHAT)} />;
      case ViewType.AUTH: return <AuthView language={user.language} onAuth={(email, username) => setUser(p => ({...p, isAuthenticated: true, email, username}))} />;
      default: return <ChatView onUseCredit={useCredit} isPro={user.isPro} language={user.language} personality={user.personality} user={user} onNavigate={setCurrentView} />;
    }
  };

  return (
    <div className="flex h-full w-full bg-white dark:bg-[#050505] text-slate-900 dark:text-white overflow-hidden transition-colors duration-300">
      <Sidebar 
        currentView={currentView} 
        onViewChange={setCurrentView} 
        isPro={user.isPro} 
        creditsUsed={user.dailyCreditsUsed} 
        language={user.language}
        onLanguageChange={(l) => setUser(p => ({...p, language: l}))} 
        user={user} 
        onSignOut={() => setUser(p => ({...p, isAuthenticated: false}))}
        isOpen={isSidebarOpen} 
        onToggle={() => setIsSidebarOpen(!isSidebarOpen)} 
        searchHistory={searchHistory} 
        onHistoryClick={(query) => {
          setCurrentView(ViewType.DICTIONARY);
        }}
        onPersonalityChange={(p) => setUser(prev => ({...prev, personality: p}))}
        onThemeChange={(t) => setUser(prev => ({...prev, theme: t}))}
      />
      <main className="flex-1 h-full w-full relative z-10 overflow-hidden flex flex-col">
        <ErrorBoundary>
          <Suspense fallback={<div className="flex h-full items-center justify-center bg-white dark:bg-[#050505]"><Loader2 className="animate-spin text-blue-500" size={32} /></div>}>
            <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-black/5 dark:border-white/5 bg-white/80 dark:bg-[#050505]/80 backdrop-blur-lg sticky top-0 z-[40] mobile-safe-top">
               <button onClick={() => setIsSidebarOpen(true)} className="p-2 -ml-2 text-slate-500 dark:text-white/40 active:scale-90 transition-transform">
                 <Menu size={20} />
               </button>
               <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
                    <Sparkles size={14} className="text-white" fill="currentColor" />
                  </div>
                  <span className="font-bold text-sm tracking-tight text-slate-900 dark:text-white uppercase">ZomiGenAI</span>
               </div>
               <div className="w-10"></div> {/* Spacer for symmetry */}
            </div>
            <div className="flex-1 overflow-hidden relative">
              {renderView()}
            </div>
          </Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
};

export default App;
